/**
 * Energy (D14, §13.2) glue between the lottery config and the kit's MotionProvider. The provider owns the
 * precedence (user CALM > lottery calm > user FULL > OS); this module only derives `lotteryCalm` from the
 * published presentation and persists the two shell-level preferences the kit does not know about.
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { PublicLotteryDto } from '@windrop/contracts';
import { useEnergy } from '@windrop/ui';

export { ENERGY_STORAGE_KEY } from '@windrop/ui';

export const TICKER_ANNOUNCE_KEY = 'windrop:ticker-announce';
export const LOCALE_OVERRIDE_KEY = 'windrop:locale';

/** The lottery-level calm switch (`presentation.energyLevel === 'CALM'`). */
export function lotteryCalm(lottery: Pick<PublicLotteryDto, 'presentation'> | null | undefined): boolean {
  return lottery?.presentation.energyLevel === 'CALM';
}

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value == null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode: preference does not persist */
  }
  for (const l of listeners) l();
}

const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  const onStorage = () => l();
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(l);
    globalThis.removeEventListener?.('storage', onStorage);
  };
};

/** A persisted boolean preference (default off) — used for the live-ticker screen-reader opt-in (§13.4). */
export function useStoredFlag(key: string): [boolean, (on: boolean) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => read(key) === 'on',
    () => false,
  );
  const set = useCallback((on: boolean) => write(key, on ? 'on' : 'off'), [key]);
  return [value, set];
}

/** Persisted locale override (`null` = negotiate). */
export function useLocaleOverride(): [string | null, (locale: string | null) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => read(LOCALE_OVERRIDE_KEY),
    () => null,
  );
  const set = useCallback((locale: string | null) => write(LOCALE_OVERRIDE_KEY, locale), []);
  return [value, set];
}

export function getLocaleOverride(): string | null {
  return read(LOCALE_OVERRIDE_KEY);
}

/** Everything the Preferences sheet edits, in one hook. */
export function usePreferences() {
  const energy = useEnergy();
  const [tickerAnnounce, setTickerAnnounce] = useStoredFlag(TICKER_ANNOUNCE_KEY);
  return { ...energy, tickerAnnounce, setTickerAnnounce };
}
